<template>
  <div>
    <menu-pages-navbar
      v-if="menuId"
      :menu-id="menuId"
      class="mb-3"
    />
    <div
      class="grow-1 mt-5"
      :class="[$vuetify.breakpoint.smAndUp ? 'mr-4' : 'mx-0']"
      :title="$t('storeMenuPageTitle')"
    >
      <!-- Header -->
      <v-slide-y-transition>
        <v-layout
          v-if="site"
          wrap
          align-start
        >
          <!-- Store Dropdown -->
          <v-menu
            :disabled="storesWithTVDecodersForMenuCategories.length < 2"
            data-cy="storemenu-selected-store-dropdown"
            lazy
            max-height="80vh"
          >
            <v-card
              slot="activator"
              class="unselectable px-3 mr-4"
              flat
            >
              <v-list
                dense
                class="list-tile-auto-height"
              >
                <v-list-tile
                  avatar
                  :ripple="false"
                >
                  <v-list-tile-avatar>
                    <v-icon
                      large
                      class="msr--text"
                    >
                      location_on
                    </v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-sub-title>
                      <small>{{ site.store.name }}</small>
                    </v-list-tile-sub-title>
                    <v-list-tile-title class="grey--text text--darken-3">
                      <span style="font-size: 1.25em">
                        <strong>{{ site.store.city }}</strong>
                        {{ site.store.location || '' }}
                      </span>
                      <div v-if="site.store.address">
                        {{ site.store.address }}
                      </div>
                    </v-list-tile-title>
                    <v-slide-y-transition>
                      <v-list-tile-sub-title
                        v-if="numTVDecodersForCurrentStore"
                      >
                        {{ numTVDecodersForCurrentStore }} x
                        <v-icon class="sub-icon">
                          tv
                        </v-icon>
                      </v-list-tile-sub-title>
                    </v-slide-y-transition>
                  </v-list-tile-content>
                  <v-list-tile-action>
                    <v-icon
                      style="font-size: 2.66em"
                      :disabled="
                        storesWithTVDecodersForMenuCategories.length < 2
                      "
                    >
                      arrow_drop_down
                    </v-icon>
                  </v-list-tile-action>
                </v-list-tile>
              </v-list>
            </v-card>
            <v-list
              dense
              class="list-tile-auto-height"
            >
              <store-tile
                v-for="(
                  store, storeIndex
                ) in storesWithTVDecodersForMenuCategories"
                :key="store.id"
                :highlight-title="store.id == site.store.id"
                :show-divider="
                  storeIndex < storesWithTVDecodersForMenuCategories.length - 1
                "
                :store="store"
                :num-tvdecoders="store.tvdecoders.length"
                divider-margin-class=""
                @click="
                  $router.push({
                    name: 'storemenu',
                    params: {storeId: store.id},
                  })
                "
              >
                <div slot="actions" />
              </store-tile>
            </v-list>
          </v-menu>
          <!-- MenuCategoryDecoders Link -->
          <v-btn
            class="subheading mb-2"
            :class="{'ml-auto': $vuetify.breakpoint.smAndUp}"
            color="grey darken-3"
            round
            flat
            :title="$t('menuCategoryDecodersPageHint')"
            :to="{name: 'menucategorydecoders', params: {menuId: menuId}}"
          >
            <v-icon left>
              shortcut
            </v-icon>
            {{ $t('storeMenuGoToMenuCategoryDecoders') }}
          </v-btn>
        </v-layout>
      </v-slide-y-transition>
      <!-- Loading Indicator -->
      <v-fade-transition>
        <v-layout
          v-if="loading"
          column
          align-center
          class="mt-5"
        >
          <v-progress-circular
            :size="50"
            indeterminate
            class="msr--text"
          />
          <v-subheader class="mt-4">
            {{ $t('previewAvailableSoon') }}...
          </v-subheader>
        </v-layout>
      </v-fade-transition>
    </div>
    <div
      v-if="!loading"
      class="mt-3"
    >
      <!-- TVDecoder Menu Previews -->
      <v-layout
        align-start
        wrap
      >
        <v-card
          v-for="(tvdecoder, tvdecoderIndex) in menuTvdecoders"
          :key="site.store.id + tvdecoder.id"
          ref="tvdecodermenupreviews"
          class="mt-3 mr-4 pb-2 unselectable transparent"
          flat
        >
          <v-layout
            align-center
            wrap
            class="subheading mb-1 pt-2"
          >
            <icon-tv-decoder
              :tvdecoder="tvdecoder"
              class="mr-2 unselectable"
            />
            <span
              class="pseudo-anchor--inverted mr-3"
              :title="$t('goToDecoderDetailPage')"
              @click.stop="
                $router.push({
                  name: 'decoder',
                  params: {ctype: 'tv', decoderId: tvdecoder.id},
                })
              "
            >{{ tvdecoder.description }}</span>
            <allowed-menu-categories-select
              ref="allowed-menucategories-selects"
              class="ml-auto"
              :tvdecoder="getTVDecoderForMenuCategorySelectById(tvdecoder.id)"
              :menu-id="site.store.menu_id"
              :label="$t('menuCategories')"
              :busy="busy"
              :warning="tvdecoder.allowed_menu_categories.length == 0"
              @allowed-menu-categories-changed="
                debouncedFetchAndUpdateDecoder(tvdecoder)
              "
              @dropdown-changed="
                (open) => (allowedMenuCategoriesDropdownOpen = open)
              "
            />
          </v-layout>
          <tv-decoder-menu-preview
            :busy="busy == tvdecoder.id"
            :center="false"
            :tvdecoder="tvdecoder"
            :preview-max-width="previewMaxWidth"
            :preview-max-height="previewMaxHeight"
            :no-preview-warning="
              tvdecoder.allowed_menu_categories.length == 0
                ? 'storeMenuAssignAtLeastOneCategoryToPlayer'
                : 'tvdecoderMenuPreviewNotPossible'
            "
            :no-preview-warning-icon="
              tvdecoder.allowed_menu_categories.length == 0
                ? 'menu_book'
                : 'warning'
            "
            class="unselectable"
            :class="{clickable: tvdecoder.allowed_menu_categories.length == 0}"
            @click.native="
              onTVDecoderMenuPreviewClicked(tvdecoder, tvdecoderIndex)
            "
          />
        </v-card>
      </v-layout>
      <!-- List of MenuItems and their state -->
      <div
        ref="menuitemsheader"
        class="mt-5 unselectable white py-2 px-3 clickable"
        style="
          scroll-margin-top: 56px;
          width: fit-content;
          display: flex;
          align-items: center;
        "
        :title="expandMenuItems ? $t('clickToCollapse') : $t('clickToExpand')"
        data-cy="toggle-menu-items-expansion"
        @click="expandMenuItems = !expandMenuItems"
      >
        <v-layout
          class="grey--text text--darken-2 mr-2"
          align-center
        >
          <v-icon
            class="mr-3"
            medium
            color="grey"
          >
            fastfood
          </v-icon>
          <div>
            <div class="title">
              {{ $t('storeMenuPageMenuItemsHeader') }}
            </div>
            <div class="caption mt-1">
              <span
                v-if="activeMenuItems.length > 0"
                class="success--text"
                style="display: inline-block"
              >
                {{ $t('menuItemStateActive') }}
                {{ activeMenuItems.length }} </span><span
                  v-if="
                    activeMenuItems.length > 0 && deactivatedMenuItems.length > 0
                  "
                >,</span>
              <span
                v-if="deactivatedMenuItems.length > 0"
                class="error--text"
                style="display: inline-block"
              >
                {{ $t('menuItemStateSoldOut') }}
                {{ deactivatedMenuItems.length }}
              </span>
            </div>
          </div>
        </v-layout>
        <v-icon
          large
          :style="{
            transform: expandMenuItems ? 'rotate(180deg)' : '',
          }"
        >
          expand_more
        </v-icon>
      </div>
      <v-slide-y-transition>
        <div v-show="expandMenuItems">
          <!-- Search -->
          <v-text-field
            v-if="menuItems.length > 5"
            v-model="searchText"
            class="mt-3"
            :label="$t('search')"
            :hint="searchHint"
            persistent-hint
            clearable
            append-icon="search"
            style="max-width: 300px"
            @click:clear="clearSearch()"
            @keyup.native.esc="clearSearch()"
          />
          <v-data-table
            v-if="!loading"
            hide-actions
            disable-initial-sort
            :headers="menuItemHeaders"
            :items="filteredMenuItems"
            :pagination.sync="menuItemTablePagination"
            class="mt-3"
            :no-data-text="$t('noDataText')"
            :no-results-text="$t('noResultsText')"
          >
            <template
              slot="items"
              slot-scope="props"
            >
              <tr
                :key="props.item.id"
                class="menu-item-row"
                :class="{
                  'bg-error':
                    !props.item.active_in_store ||
                    !!props.item.deactivated_until,
                }"
              >
                <!-- State (active/inactive, can be temporary) -->
                <td
                  width="10%"
                  :style="{
                    'min-width': $vuetify.breakpoint.lgAndUp
                      ? '345px'
                      : 'initial',
                  }"
                >
                  <v-menu
                    lazy
                    style="width: 100%"
                  >
                    <!-- Activator button showing current state -->
                    <v-btn
                      slot="activator"
                      flat
                      round
                      class="ma-0"
                      :title="$t('menuItemChangeState')"
                    >
                      <!-- State: Active -->
                      <span
                        v-if="props.item.active_in_store"
                        class="success--text"
                      >
                        <v-icon>check</v-icon>
                      </span>
                      <!-- State: Sold out -->
                      <span
                        v-else-if="!props.item.deactivated_until"
                        class="error--text"
                      >{{ $t('menuItemStateSoldOut') }}</span>
                      <!-- State: Sold out until date -->
                      <span
                        v-else
                        class="error--text"
                      >{{ $t('menuItemStateSoldOutUntil') }}
                        {{
                          subtractOneDayFromDate(props.item.deactivated_until)
                            | dayjs('DD.MM.YYYY')
                        }}
                      </span>
                      <!-- Dropdown Icon -->
                      <v-icon
                        class="btn-state--dropdown-icon ml-1"
                        medium
                      >
                        arrow_drop_down
                      </v-icon>
                    </v-btn>
                    <!-- Dropdown options -->
                    <v-list>
                      <!-- Set active -->
                      <v-list-tile
                        v-if="!props.item.active_in_store"
                        @click="activateMenuItem(props.item)"
                      >
                        {{ $t('menuItemStateActive') }}
                      </v-list-tile>
                      <v-divider v-if="!props.item.active_in_store" />
                      <!-- Set sold-out -->
                      <v-list-tile @click="deactivateMenuItem(props.item)">
                        {{ $t('menuItemStateSoldOut') }}
                      </v-list-tile>
                      <!-- Set sold-out until tomorrow -->
                      <v-list-tile
                        @click="deactivateMenuItemUntilTomorrow(props.item)"
                      >
                        {{ $t('menuItemStateSoldOutUntilTomorrow') }}
                      </v-list-tile>
                      <!-- Set sold-out until specific date -->
                      <v-list-tile
                        @click="openDatePickerForMenuItem($event, props.item)"
                      >
                        {{ $t('menuItemStateSoldOutUntilDate') }}
                      </v-list-tile>
                    </v-list>
                  </v-menu>
                </td>
                <!-- Category -->
                <td style="width: 200px">
                  <v-chip
                    label
                    class="
                      grey
                      lighten-3
                      body-1
                      outlined--dark
                      ml-0
                      mr-3
                      pseudo-anchor--inverted
                      unselectable
                      clickable
                    "
                    :title="$t('goToMenuCategoryMenuItems')"
                    @click="
                      $router.push({
                        name: 'menuitems',
                        params: {menuId: menuId},
                        query: {category: props.item.category.id},
                      })
                    "
                  >
                    <v-badge color="transparent">
                      <!-- Timeframes Indicator -->
                      <span
                        v-if="(props.item.category.timeframes || []).length > 0"
                        slot="badge"
                      >
                        <v-icon
                          style="font-size: 16px"
                          :color="
                            props.item.category.active_now ? 'success' : 'error'
                          "
                          :title="
                            props.item.category.active_now
                              ? $t('menuCategoryTimeframesActiveNow')
                              : $t('menuCategoryTimeframesInactiveNow')
                          "
                        >
                          timelapse
                        </v-icon>
                      </span>
                      {{ props.item.category.name }}
                    </v-badge>
                  </v-chip>
                </td>
                <!-- Name -->
                <td>
                  <router-link
                    class="
                      body-1
                      pseudo-anchor--inverted
                      black--text
                      unselectable
                    "
                    :to="{
                      name: 'editmenuitem',
                      params: {menuItemId: props.item.id},
                      query: {menu: menuId, expand_stores: true},
                    }"
                    :title="$t('editmenuitem')"
                  >
                    {{ props.item.name }}
                  </router-link>
                </td>
              </tr>
            </template>
          </v-data-table>
          <!-- Date Picker for deactivated_until -->
          <v-menu
            v-model="showDeactivationUntilMenu"
            :close-on-content-click="false"
            lazy
            min-width="290px"
            absolute
            :position-x="deactivationUntilMenuPositionX"
            :position-y="deactivationUntilMenuPositionY"
          >
            <v-date-picker
              v-model="deactivationUntilDate"
              color="msr"
              scrollable
              :allowed-dates="isNotInPast"
            >
              <v-layout column>
                <div
                  v-if="deactivationUntilMenuItem"
                  class="mx-3 mb-3"
                >
                  <div class="title font-weight-bold mb-2">
                    {{ deactivationUntilMenuItem.name }}
                  </div>
                  <span v-if="!deactivationUntilDate">{{
                    $t('menuItemStateSoldOutUntilDate')
                  }}</span>
                  <span
                    v-else
                  >{{ $t('menuItemStateSoldOutUntil') }}
                    {{ deactivationUntilDate | dayjs('DD.MM.YYYY') }}</span>
                </div>
                <v-layout>
                  <v-spacer />
                  <v-btn
                    flat
                    round
                    color="black"
                    @click="onDeactivationUntilDateCancelled()"
                  >
                    {{ $t('close') }}
                  </v-btn>
                  <v-btn
                    :disabled="!deactivationUntilDate"
                    round
                    color="msr"
                    class="white--text"
                    @click="
                      onDeactivationUntilDateChosen(
                        deactivationUntilMenuItem,
                        deactivationUntilDate
                      )
                    "
                  >
                    {{ $t('save') }}
                  </v-btn>
                  <v-spacer />
                </v-layout>
              </v-layout>
            </v-date-picker>
          </v-menu>
        </div>
      </v-slide-y-transition>
    </div>
    <!-- Scroll Spacer -->
    <div style="min-height: 60px" />
  </div>
</template>

<script>
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import {mapState} from 'vuex';

import {removeQueryParamFromCurrentRoute} from '@/utils';

import matchesFilter from '@/matchesFilter';
import activateMenuItem from '@/mixins/activateMenuItem.js';
import timing from '@/mixins/timing.js';

import AllowedMenuCategoriesSelect from '@/components/AllowedMenuCategoriesSelect';
import IconTvDecoder from '@/components/IconTvDecoder';
import MenuPagesNavbar from '@/components/Menu/MenuPagesNavbar';
import StoreTile from '@/components/StoreTile';
import TvDecoderMenuPreview from '@/components/DecoderDetail/TvDecoderMenuPreview';

export default {
  components: {
    AllowedMenuCategoriesSelect,
    IconTvDecoder,
    MenuPagesNavbar,
    StoreTile,
    TvDecoderMenuPreview,
  },
  mixins: [activateMenuItem, timing],
  data() {
    return {
      loading: false,
      busy: false, // Either false or <TVDecoder.id> to control specific loading indicator.
      searchText: '',

      // Note: See MenuItemStores.vue for this functionality from the
      //   'opposite side'.
      showDeactivationUntilMenu: null,
      deactivationUntilMenuItem: null,
      deactivationUntilMenuPositionX: 0,
      deactivationUntilMenuPositionY: 0,
      deactivationUntilDate: null,

      allowedMenuCategoriesDropdownOpen: false,

      menuItemHeaders: [
        {
          text: this.$t('status'),
          value: 'active_in_store',
          sortable: true,
        },
        {
          text: this.$t('menuCategory'),
          value: 'category.name',
          sortable: true,
        },
        {
          text: this.$t('name'),
          value: 'name',
          sortable: true,
        },
      ],
      menuItemTablePagination: {
        rowsPerPage: -1, // 'All'
      },

      // From backend.
      menuTvdecodersForSelects: [],
      menuItems: [],
      site: null,
    };
  },
  computed: {
    ...mapState({
      menuCategories: (state) => state.common.menuCategories,
      // Note: This is fetched via the MenuPagesNavbar component.
      storesWithTVDecodersForMenuCategories: (state) =>
        state.common.storesWithTVDecodersForMenuCategories,
    }),
    /**
     * Return number of TVDecoders for the current store.
     *
     * Note: The site.store carries no tvdecoders list, but we have it
     *   in the stores list, so get it from there.
     */
    numTVDecodersForCurrentStore() {
      if (this.site && this.site.store) {
        for (const store of this.storesWithTVDecodersForMenuCategories) {
          if (store.id == this.site.store.id) {
            return store.tvdecoders.length;
          }
        }
      }
      return 0;
    },
    activeMenuItems() {
      return this.menuItems.filter((menuItem) => {
        return menuItem.active_in_store;
      });
    },
    deactivatedMenuItems() {
      return this.menuItems.filter((menuItem) => {
        return !menuItem.active_in_store;
      });
    },
    expandMenuItems: {
      get() {
        return this.$store.state.common.storeMenuExpandMenuItems;
      },
      set(expand) {
        this.$store.commit('SET_STORE_MENU_EXPAND_MENU_ITEMS', expand);
      },
    },
    menuId() {
      if (!this.site) {
        return null;
      }
      return this.site.store.menu_id;
    },
    menuTvdecoders() {
      if (!this.site) {
        return [];
      }
      return this.site.tvdecoders.filter(
        (tvdecoder) => tvdecoder.is_menu_player
      );
    },
    /**
     * Match MenuItems against search terms.
     */
    filteredMenuItems() {
      if (!this.searchText) {
        return this.menuItems;
      }

      return this.menuItems.filter((menuItem) => {
        return (
          matchesFilter(menuItem.category.name, this.searchText) ||
          matchesFilter(menuItem.name, this.searchText)
        );
      });
    },
    searchHint() {
      if (!this.searchText) {
        return `${this.menuItems.length} ${this.$t('menuItems')}`;
      }
      return `${this.filteredMenuItems.length}/${
        this.menuItems.length
      } ${this.$t('menuItems')}`;
    },
    previewMaxWidth() {
      const availableWidth = 1090;
      if (this.site && this.menuTvdecoders.length == 1) {
        return availableWidth / 1.33;
      }
      return availableWidth / 2;
    },
    previewMaxHeight() {
      return this.previewMaxWidth;
    },
  },
  watch: {
    allowedMenuCategoriesDropdownOpen: function () {
      // Note: Just sync on any change, as clicking on another dropdown
      //   activator when a dropdown is opened already may screw up the
      //   global 'opened' state aka we can't reliable sync on 'closed'.
      this.syncMenuTVDecoderLists();
    },
    /**
     * Handle expansion and scrolling if specified in URL query params.
     */
    menuItems: function () {
      if (this.menuItems.length == 0) {
        return;
      }

      if (this.$route.query && this.$route.query.expand_menu_items) {
        this.expandMenuItems = true;
        removeQueryParamFromCurrentRoute('expand_menu_items');

        setTimeout(() => {
          this.$refs.menuitemsheader.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      }
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    /**
     * Copy tvdecoders used for buttons in dropdown from API results.
     *
     * We keep two parallel lists of TVDecoders, so that toggling the
     * dropdown does trigger a rerender of the preview, but without
     * affecting the dropdown itself on top of our explicit frontend
     * update (prevents flickering, race conditions with multiple
     * requests etc.).
     *
     * The lists are kept separate as we edit category assignments in
     * the dropdown, and synced afterwards as the dropdown is closed.
     */
    syncMenuTVDecoderLists() {
      this.menuTvdecodersForSelects = this.menuTvdecoders.slice();
    },
    getTVDecoderForMenuCategorySelectById(tvdecoderId) {
      return this.menuTvdecodersForSelects.filter(
        (decoder) => decoder.id == tvdecoderId
      )[0];
    },
    onTVDecoderMenuPreviewClicked(tvdecoder, tvdecoderIndex) {
      if (tvdecoder.allowed_menu_categories.length > 0) {
        return;
      }

      const select =
        this.$refs['allowed-menucategories-selects'][tvdecoderIndex];
      select.openMenu();
    },
    getTVDecoderMenuPreviewRef(tvdecoderIndex) {
      return this.$refs.tvdecodermenupreviews[tvdecoderIndex];
    },
    openDatePickerForMenuItem(event, menuItem) {
      this.deactivationUntilMenuPositionX = event.clientX;
      this.deactivationUntilMenuPositionY = event.clientY;

      this.deactivationUntilMenuItem = menuItem;

      // Initialize with existing date value or keep the one that was
      // last used. This helps to assign the same date value to multiple
      // MenuItems quickly.
      if (!!menuItem.deactivated_until) {
        this.deactivationUntilDate = menuItem.deactivated_until;
      }

      this.showDeactivationUntilMenu = true;
    },
    onDeactivationUntilDateChosen(menuItem, untilDateString) {
      const vm = this;
      const toggleBusy = false;

      this.deactivateMenuItemOnStores(
        menuItem,
        [this.site.store],
        vm.fetchMenuItemsForStore,
        toggleBusy,
        untilDateString
      );

      this.onDeactivationUntilDateCancelled();
    },
    onDeactivationUntilDateCancelled() {
      this.deactivationUntilMenuItem = null;
      this.showDeactivationUntilMenu = false;
    },
    clearSearch() {
      this.searchText = '';
    },
    activateMenuItem(menuItem) {
      const vm = this;
      const payload = Object.assign({}, menuItem);

      // Frontend update
      menuItem.active_in_store = true;
      menuItem.deactivated_until = null;

      // Backend update
      this.activateMenuItemOnStores(
        payload,
        [this.site.store],
        vm.debouncedFetchSite
      );
    },
    deactivateMenuItem(menuItem, until = null) {
      const vm = this;
      const toggleBusy = false;

      const menuItemPayload = Object.assign({}, menuItem);
      menuItemPayload.until = until;

      // Frontend update
      menuItem.active_in_store = false;
      if (until) {
        // Add one day to mimic what the backend does, since the display
        // will subtract one day before showing the value.
        const untilPlusOneString = new dayjs(until)
          .add(1, 'day')
          .format('YYYY-MM-DD');
        menuItem.deactivated_until = untilPlusOneString;
      } else {
        menuItem.deactivated_until = null;
      }

      this.deactivateMenuItemOnStores(
        menuItemPayload,
        [this.site.store],
        vm.debouncedFetchSite,
        toggleBusy,
        until
      );
    },
    deactivateMenuItemUntilTomorrow(menuItem) {
      const includingTodayString = new dayjs().format('YYYY-MM-DD');
      return this.deactivateMenuItem(menuItem, includingTodayString);
    },
    fetchData({indicator = true} = {}) {
      if (indicator) {
        this.loading = true;
      }

      this.fetchSite().then(() => {
        this.$store.commit('SET_LAST_STORE_WITH_MENU_VISITED', this.site.store);
        this.syncMenuTVDecoderLists();
        this.fetchMenuItemsForStore().finally(() => {
          this.loading = false;
        });
      });

      // Needed for MenuCategory assignment to TVDecoders.
      this.$store.dispatch('LIST_MENU_CATEGORIES');
    },
    fetchSite() {
      return this.$api.retrieveSite(this.$route.params.storeId).then((site) => {
        this.site = site;
      });
    },
    debouncedFetchSite: debounce(function () {
      this.fetchSite();
    }, 300),
    /**
     * Get the latest state of the TVDecoder, including menu_pages preview.
     *
     * This will cancel the previous request so we don't get delayed
     * outdated updates, as the preview creation takes some time.
     */
    debouncedFetchAndUpdateDecoder: debounce(function (tvdecoder) {
      const tvdecoderId = tvdecoder.id;
      this.busy = tvdecoderId;

      // We control cancellation for this case here, we don't want
      // $api.retrieveDecoder() to always cancel as it is used in a
      // number of different use cases where this may be undesired.
      this.$api.cancelCurrentRequest();

      this.$api
        .retrieveDecoder('tv', tvdecoderId)
        .then((response) => {
          const newTvdecoder = response.data;

          for (let i = 0; i < this.site.tvdecoders.length; i++) {
            const oldTvdecoder = this.site.tvdecoders[i];
            if (oldTvdecoder.id == tvdecoderId) {
              this.$set(this.site.tvdecoders, i, newTvdecoder);
              break;
            }
          }

          this.busy = false;

          // Also update the list of active MenuItems which the category
          // change most likely has affected.
          this.fetchMenuItemsForStore();
        })
        .catch((error) => {
          if (error && error.message && error.name == 'CanceledError') {
            console.log(
              '[DEBUG] Outdated TVDecoder menu page preview request cancelled'
            );
          }
        });
    }, 300),
    fetchMenuItemsForStore() {
      const storeId = this.site.store.id;
      return this.$api.listMenuItems(this.menuId, storeId).then((response) => {
        this.menuItems = response.data;
      });
    },
  },
};
</script>

<style scoped>
.btn-state--dropdown-icon {
  opacity: 0;
}
.menu-item-row:hover .btn-state--dropdown-icon {
  opacity: 1;
}

.category-in-use {
  outline: 2px solid rgba(76, 175, 80, 1) !important;
}
</style>



// WEBPACK FOOTER //
// src/pages/StoreMenu.vue